import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Sparkles, ChevronRight, MapPin as MapPinIcon, SlidersHorizontal, Check } from "lucide-react";
import { STATUSES, PILL_COLORS } from "./constants";
import { ProspectRow } from "./PipelineRows";

const FOLLOWUP_META = {
  urgent: { label: "Urgent", bg: "bg-rose-500/15", border: "border-rose-500/30", text: "text-rose-300", dot: "bg-rose-400" },
  week: { label: "Cette semaine", bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-300", dot: "bg-amber-400" },
  later: { label: "Peut attendre", bg: "bg-sky-500/15", border: "border-sky-500/30", text: "text-sky-300", dot: "bg-sky-400" },
};

export function PipelineKanbanView({
  items, currentList, counts, activeStatuses, toggleStatus,
  filterOpen, setFilterOpen, setActiveStatuses,
  analyzing, analyze, followups, navigate, pendingIds,
}) {
  return (
    <>
      {/* Smart follow-up CTA */}
      <div className="px-5">
        <button
          onClick={analyze}
          disabled={analyzing}
          data-testid="analyze-followups"
          className="w-full p-4 flex items-center gap-3 rounded-xl border border-emerald-500/40 hover:border-emerald-500 active:scale-[0.99] transition-colors disabled:opacity-70"
          style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.18) 0%, rgba(0,220,130,0.06) 100%)" }}
        >
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 grid place-items-center shrink-0">
            <Sparkles className="w-4 h-4 text-emerald-300" strokeWidth={1.8} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold text-sm text-white">{analyzing ? "Analyse en cours…" : "Priorités Prosperr"}</div>
            <div className="text-xs text-zinc-400 mt-0.5">Relances urgentes et plan de la semaine</div>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      {/* Filter bar */}
      <div className="mt-5 px-5">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilterOpen(true)}
            data-testid="open-status-filter"
            className="hp-btn hp-btn-secondary rounded-full shrink-0"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>Filtrer</span>
            {activeStatuses.size < STATUSES.length && (
              <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-semibold grid place-items-center leading-none tabular-nums">
                {activeStatuses.size}
              </span>
            )}
          </button>
          <div className="flex gap-1.5 overflow-x-auto flex-wrap no-scrollbar">
            {STATUSES.filter(s => activeStatuses.has(s)).map((s) => {
              const c = PILL_COLORS[s];
              return (
                <span key={s} data-testid={`active-filter-${s}`} className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border ${c.bg} ${c.text} ${c.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                  {s}
                  <span className="text-[10px] opacity-70 ml-0.5 tabular-nums">{counts[s]}</span>
                  {activeStatuses.size > 1 && (
                    <button onClick={() => toggleStatus(s)} className="ml-0.5 opacity-70 hover:opacity-100" aria-label={`Retirer ${s}`}>×</button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filter sheet */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl bg-[var(--surface)] border-t border-[var(--border)] text-[var(--text)]">
          <SheetHeader className="pb-1">
            <SheetTitle className="text-left text-[var(--text)]">Filtrer par statut</SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-1.5">
            {STATUSES.map((s) => {
              const c = PILL_COLORS[s];
              const checked = activeStatuses.has(s);
              return (
                <button
                  key={s}
                  onClick={() => toggleStatus(s)}
                  data-testid={`filter-check-${s}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left border ${
                    checked ? `${c.bg} ${c.border}` : "bg-[var(--surface-alt)] border-[var(--border-soft)] hover:border-zinc-700"
                  }`}
                >
                  <span className={`w-5 h-5 rounded-md border grid place-items-center shrink-0 transition-all ${
                    checked ? "bg-emerald-500 border-emerald-500" : "border-[var(--border)] bg-[var(--bg)]"
                  }`}>
                    {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                  <span className={`flex items-center gap-2 flex-1 text-[13px] font-medium ${checked ? c.text : "text-zinc-400"}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${c.dot}`} />
                    {s}
                  </span>
                  <span className="text-xs font-semibold text-zinc-500 tabular-nums">{counts[s]}</span>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { setActiveStatuses(new Set(STATUSES)); setFilterOpen(false); }}
            className="w-full mt-4 py-3 rounded-lg border border-[var(--border)] text-sm font-medium text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
          >
            Tout sélectionner
          </button>
        </SheetContent>
      </Sheet>

      {/* Lead list */}
      <div className="px-5 mt-4">
        {currentList.length === 0 ? (
          <div className="hp-card p-10 text-center">
            <div className="w-14 h-14 rounded-xl bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center mx-auto">
              <Plus className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
            </div>
            <div className="font-display font-semibold text-[var(--text)] mt-4">Aucun lead ici</div>
            <div className="text-sm text-zinc-500 mt-1">Lancez une session Road Maps pour en ajouter</div>
            <Link to="/radar">
              <button className="hp-btn hp-btn-primary mt-5" data-testid="empty-radar-cta">
                <MapPinIcon className="w-3.5 h-3.5" /> Road Maps
              </button>
            </Link>
          </div>
        ) : (
          <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]">
            {currentList.map((p) => (
              <ProspectRow
                key={p.prospect_id}
                p={p}
                pending={pendingIds.has(p.prospect_id)}
                onClick={() => navigate(`/prospect/${p.prospect_id}`)}
                navigate={navigate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Follow-ups output */}
      {followups && (
        <div className="px-5 mt-6" data-testid="followups-output">
          <div className="hp-label mb-3">Priorités Prosperr</div>
          {["urgent", "week", "later"].map((k) => {
            const group = followups[k] || [];
            if (!group.length) return null;
            const meta = FOLLOWUP_META[k];
            return (
              <div key={k} className="mt-4">
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border ${meta.bg} ${meta.border} ${meta.text} mb-2`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {meta.label}
                </div>
                <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]">
                  {group.map((g) => {
                    const pr = items.find((x) => x.prospect_id === g.prospect_id);
                    return (
                      <button
                        key={g.prospect_id || `${k}-${g.raison}`}
                        onClick={() => pr && navigate(`/prospect/${pr.prospect_id}`)}
                        className="w-full text-left p-4 hover:bg-[var(--surface-alt)]/60 transition-colors"
                      >
                        <div className="font-medium text-[14px] text-[var(--text)]">{pr?.name || g.prospect_id}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{g.raison}</div>
                        <div className="text-xs text-emerald-400 font-medium mt-1.5">→ {g.action} <span className="text-zinc-600 font-normal">({g.canal})</span></div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
